// Studio Jen Beauty Booking System
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
} from '@mui/material';
import { authAPI, customerAuthAPI } from './utils/api';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import SpaIcon from '@mui/icons-material/Spa';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SettingsIcon from '@mui/icons-material/Settings';
import WebIcon from '@mui/icons-material/Web';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import theme from './theme';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import BookingPage from './pages/BookingPage';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import TimeSlotManagement from './pages/TimeSlotManagement';
import AdminServices from './pages/AdminServices';
import ReportsPage from './pages/ReportsPage';
import CustomerHistory from './pages/CustomerHistory';
import AdminCustomers from './pages/AdminCustomers';
import AdminBookingCreate from './pages/AdminBookingCreate';
import CustomerLogin from './pages/CustomerLogin';
import CustomerPortal from './pages/CustomerPortal';
import VerifyMagicLink from './pages/VerifyMagicLink';
import AdminLoyalty from './pages/AdminLoyalty';
import AdminDiscountCodes from './pages/AdminDiscountCodes';
import AdminSettings from './pages/AdminSettings';
import AdminSiteSettings from './pages/AdminSiteSettings';
import PoliciesPage from './pages/PoliciesPage';

// Mobile Bottom Navigation Component
function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const moreMenuOpen = Boolean(moreAnchorEl);

  // "More" menu items
  const moreMenuItems = [
    { path: '/admin/reports', label: 'Reports', icon: <AssessmentIcon /> },
    { path: '/admin/loyalty', label: 'Loyalty', icon: <LoyaltyIcon /> },
    { path: '/admin/discount-codes', label: 'Promo Codes', icon: <LocalOfferIcon /> },
    { path: '/admin/site-settings', label: 'Site Content', icon: <WebIcon /> },
    { path: '/admin/settings', label: 'Account', icon: <SettingsIcon /> },
  ];

  // Check if current path is in "More" menu
  const isMorePath = moreMenuItems.some(item => location.pathname === item.path);

  // Map paths to nav indices (0-3 for main nav, 4 for "More")
  const getNavValue = () => {
    if (location.pathname === '/admin') return 0;
    if (location.pathname.startsWith('/admin/customer')) return 1;
    if (location.pathname === '/admin/slots') return 2;
    if (location.pathname === '/admin/services') return 3;
    if (isMorePath) return 4; // Highlight "More" when on a more menu path
    return 0;
  };

  const handleNavChange = (event, newValue) => {
    if (newValue === 4) {
      // "More" clicked - open menu
      setMoreAnchorEl(event.currentTarget);
    } else {
      const paths = ['/admin', '/admin/customers', '/admin/slots', '/admin/services'];
      navigate(paths[newValue]);
    }
  };

  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };

  const handleMoreMenuItemClick = (path) => {
    navigate(path);
    setMoreAnchorEl(null);
  };

  if (!isMobile) return null;

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={getNavValue()}
          onChange={handleNavChange}
          showLabels
          sx={{
            height: 56,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 60,
              maxWidth: 80,
              padding: '4px 0px',
              '&.Mui-selected': {
                paddingTop: '4px',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              '&.Mui-selected': {
                fontSize: '0.7rem',
              },
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.3rem',
            },
          }}
        >
          <BottomNavigationAction label="Home" icon={<DashboardIcon />} />
          <BottomNavigationAction label="Clients" icon={<PeopleIcon />} />
          <BottomNavigationAction label="Slots" icon={<EventIcon />} />
          <BottomNavigationAction label="Services" icon={<SpaIcon />} />
          <BottomNavigationAction label="More" icon={<MoreHorizIcon />} />
        </BottomNavigation>
      </Paper>

      {/* More Menu */}
      <Menu
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMoreMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 200,
            mb: 1,
          },
        }}
      >
        {moreMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => handleMoreMenuItemClick(item.path)}
            selected={location.pathname === item.path}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(139, 38, 53, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// Admin Layout with Sidebar
function AdminLayout({ children }) {
  const location = useLocation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const navItems = [
    { path: '/admin', label: 'Dashboard' },
    { path: '/admin/customers', label: 'Customers' },
    { path: '/admin/slots', label: 'Time Slots' },
    { path: '/admin/services', label: 'Services' },
    { path: '/admin/reports', label: 'Reports' },
    { path: '/admin/loyalty', label: 'Loyalty' },
    { path: '/admin/discount-codes', label: 'Promo Codes' },
    { path: '/admin/site-settings', label: 'Site Content' },
    { path: '/admin/settings', label: 'Account' },
  ];

  return (
    <div className="admin-layout">
      {/* Only show sidebar on desktop */}
      {!isMobile && (
        <aside className="admin-sidebar">
          <nav>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </aside>
      )}
      <div className="admin-content" style={{ paddingBottom: isMobile ? '80px' : '0' }}>
        {children}
      </div>
      {/* Mobile bottom navigation */}
      <MobileBottomNav />
    </div>
  );
}

// Inner App component that can use hooks
function AppContent({ user, handleLogout, handleLogin, onUserUpdate, customer, handleCustomerLogin, handleCustomerLogout }) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const idleTimerRef = useRef(null);

  // Idle timeout - 10 minutes (600000ms)
  const IDLE_TIMEOUT = 10 * 60 * 1000;

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    // Only set timer if admin user is logged in
    if (user) {
      idleTimerRef.current = setTimeout(() => {
        // Auto-logout due to inactivity
        handleLogout();
        navigate('/admin/login');
        // Show a message (using alert since toast might not be visible)
        alert('You have been logged out due to inactivity.');
      }, IDLE_TIMEOUT);
    }
  }, [user, handleLogout, navigate, IDLE_TIMEOUT]);

  // Set up idle timeout tracking
  useEffect(() => {
    if (!user) {
      // Clear timer if user logs out
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      return;
    }

    // Events to track for user activity
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Reset timer on any activity
    const handleActivity = () => {
      resetIdleTimer();
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Start the initial timer
    resetIdleTimer();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [user, resetIdleTimer]);

  // Don't show main navbar on customer portal pages
  const isPortalPage = location.pathname.startsWith('/portal');

  return (
    <div className="app">
      {/* Navigation - hidden on customer portal pages */}
      {!isPortalPage && (
        <nav className="navbar">
          <div className="navbar-content">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <h1>Studio Jen</h1>
            </Link>
            <nav>
              <Link to="/">Book Appointment</Link>
              <Link to="/portal">My Account</Link>
              {user ? (
                <>
                  {/* Hide admin nav links on mobile - we have bottom nav */}
                  {!isMobile && (
                    <>
                      <Link to="/admin">Dashboard</Link>
                      <Link to="/admin/customers">Customers</Link>
                      <Link to="/admin/slots">Time Slots</Link>
                      <Link to="/admin/services">Services</Link>
                      <Link to="/admin/reports">Reports</Link>
                      <Link to="/admin/loyalty">Loyalty</Link>
                      <Link to="/admin/discount-codes">Promo Codes</Link>
                    </>
                  )}
                  <button onClick={handleLogout}>
                    Logout{!isMobile && ` (${user.username})`}
                  </button>
                </>
              ) : (
                <Link to="/admin/login">Admin Login</Link>
              )}
            </nav>
          </div>
        </nav>
      )}

      {/* Routes */}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<BookingPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route
          path="/admin/login"
          element={
            user ? <Navigate to="/admin" /> : <LoginPage onLogin={handleLogin} />
          }
        />

        {/* Protected Admin Routes with Sidebar */}
        <Route
          path="/admin"
          element={user ? <AdminLayout><AdminDashboard /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/slots"
          element={user ? <AdminLayout><TimeSlotManagement /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/services"
          element={user ? <AdminLayout><AdminServices /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/reports"
          element={user ? <AdminLayout><ReportsPage /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/customers"
          element={user ? <AdminLayout><AdminCustomers /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/customer/:customerId"
          element={user ? <AdminLayout><CustomerHistory /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/booking/create"
          element={user ? <AdminLayout><AdminBookingCreate /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/loyalty"
          element={user ? <AdminLayout><AdminLoyalty /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/discount-codes"
          element={user ? <AdminLayout><AdminDiscountCodes /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/settings"
          element={user ? <AdminLayout><AdminSettings user={user} onUserUpdate={onUserUpdate} /></AdminLayout> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/site-settings"
          element={user ? <AdminLayout><AdminSiteSettings /></AdminLayout> : <Navigate to="/admin/login" />}
        />

        {/* Customer Portal Routes */}
        <Route
          path="/portal/login"
          element={customer ? <Navigate to="/portal" /> : <CustomerLogin onLogin={handleCustomerLogin} />}
        />
        <Route
          path="/portal/verify"
          element={<VerifyMagicLink onLogin={handleCustomerLogin} />}
        />
        <Route
          path="/portal"
          element={customer ? <CustomerPortal onLogout={handleCustomerLogout} /> : <Navigate to="/portal/login" />}
        />
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyAuth = async () => {
      // Check if admin user is logged in
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid on the server
          const response = await authAPI.verify();
          setUser(response.data.user);
          // Update stored user in case it changed
          localStorage.setItem('user', JSON.stringify(response.data.user));
        } catch (error) {
          // Token is invalid or expired - clear storage
          console.log('Admin token invalid, clearing auth state');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }

      // Check if customer is logged in
      const customerToken = localStorage.getItem('customerToken');
      const storedCustomer = localStorage.getItem('customer');

      if (customerToken && storedCustomer) {
        try {
          // Verify customer token is still valid
          const response = await customerAuthAPI.getProfile();
          setCustomer(response.data);
          localStorage.setItem('customer', JSON.stringify(response.data));
        } catch (error) {
          // Customer token is invalid or expired - clear storage
          console.log('Customer token invalid, clearing auth state');
          localStorage.removeItem('customerToken');
          localStorage.removeItem('customer');
          setCustomer(null);
        }
      }

      setLoading(false);
    };

    verifyAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const handleCustomerLogin = (customerData) => {
    setCustomer(customerData);
  };

  const handleCustomerLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customer');
    setCustomer(null);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <Router>
          <AppContent
            user={user}
            handleLogout={handleLogout}
            handleLogin={handleLogin}
            onUserUpdate={setUser}
            customer={customer}
            handleCustomerLogin={handleCustomerLogin}
            handleCustomerLogout={handleCustomerLogout}
          />
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
